/*
 * Pickup scheduler: two independent slots (bagels, catering) on one order.
 *
 * Each slot has its own lead time, its own date + time inputs, and its own pair
 * of cart attributes, so one order can be collected in two goes. Slots marked
 * data-required="true" (the cart actually contains that kind of item) must be
 * filled before checkout.
 *
 * Checkout buttons carrying [data-requires-pickup-slot] are disabled until every
 * required slot is complete.
 */
if (!customElements.get('pickup-scheduler')) {
  customElements.define(
    'pickup-scheduler',
    class PickupScheduler extends HTMLElement {
      connectedCallback() {
        this.status = this.querySelector('[data-pickup-status]');
        this.updateUrl = this.dataset.updateUrl || '/cart/update.js';
        this.orderType = this.dataset.orderType || 'Unknown';
        this.openHour = parseInt(this.dataset.openHour, 10);
        this.closeHour = parseInt(this.dataset.closeHour, 10);
        this.interval = parseInt(this.dataset.interval, 10) || 15;

        /* Days the shop is open, as JS weekday numbers (Sunday=0). An empty or
           unparseable list means "no restriction" - a bad config must never
           leave customers unable to book at all. */
        this.openDays = new Set(
          String(this.dataset.openDays || '')
            .split(',')
            .map((n) => parseInt(n, 10))
            .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6)
        );
        this.openDaysLabel = this.dataset.openDaysLabel || '';

        if (Number.isNaN(this.openHour)) this.openHour = 10;
        if (Number.isNaN(this.closeHour)) this.closeHour = 18;

        this.slots = Array.from(this.querySelectorAll('[data-slot]'))
          .map((el) => this.initSlot(el))
          .filter(Boolean);

        this.initFulfilment();

        this.syncCheckoutState();
      }

      /* Local YYYY-MM-DD. Deliberately not toISOString(), which shifts to UTC
         and can hand back yesterday for anyone west of Greenwich. */
      static formatDate(date) {
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${date.getFullYear()}-${month}-${day}`;
      }

      /* Pickup-or-delivery choice plus the delivery address. The address is
         only meaningful - and only required - when Delivery is chosen. */
      initFulfilment() {
        this.fulfilmentInputs = Array.from(this.querySelectorAll('[data-fulfilment-option]'));
        this.addressWrap = this.querySelector('[data-address-wrap]');
        this.addressInput = this.querySelector('[data-delivery-address]');

        const onChange = this.onChange.bind(this);
        this.fulfilmentInputs.forEach((input) => {
          input.addEventListener('change', () => {
            this.syncAddressVisibility();
            onChange();
          });
        });

        // 'change' not 'input': fires on blur, so typing an address is not one
        // cart update per keystroke.
        if (this.addressInput) this.addressInput.addEventListener('change', onChange);

        this.syncAddressVisibility();
      }

      get fulfilment() {
        const picked = this.fulfilmentInputs && this.fulfilmentInputs.find((i) => i.checked);
        return picked ? picked.value : 'Pickup';
      }

      get deliveryAddress() {
        return this.addressInput ? this.addressInput.value.trim() : '';
      }

      get addressMissing() {
        return this.fulfilment === 'Delivery' && this.deliveryAddress === '';
      }

      syncAddressVisibility() {
        if (!this.addressWrap) return;
        this.addressWrap.hidden = this.fulfilment !== 'Delivery';
      }

      /* Parses YYYY-MM-DD as a LOCAL date. new Date('2026-10-15') is parsed as
         UTC, which lands on the previous evening west of Greenwich - and so
         reports the wrong weekday, which is exactly what we test here. */
      static parseLocalDate(value) {
        const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
        if (!parts) return null;
        return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
      }

      isOpenDay(date) {
        if (!date) return false;
        if (!this.openDays || this.openDays.size === 0) return true;
        return this.openDays.has(date.getDay());
      }

      /* First open day on or after the given date. Gives up after two weeks so
         a misconfigured list cannot loop forever. */
      firstOpenOnOrAfter(date) {
        const cursor = new Date(date.getTime());
        for (let i = 0; i < 14; i += 1) {
          if (this.isOpenDay(cursor)) return cursor;
          cursor.setDate(cursor.getDate() + 1);
        }
        return date;
      }

      closedDayMessage() {
        const label = this.openDaysLabel || 'our opening days';
        return `We are only open ${label}. Please choose one of those days.`;
      }

      initSlot(el) {
        const dateInput = el.querySelector('[data-pickup-date]');
        const timeSelect = el.querySelector('[data-pickup-time]');
        const timeInput = timeSelect && timeSelect.querySelector('[data-time-value]');
        if (!dateInput || !timeSelect || !timeInput) return null;

        const leadDays = parseInt(el.dataset.leadDays, 10);
        const slot = {
          el,
          dateInput,
          timeSelect,
          timeInput,
          leadDays: Number.isNaN(leadDays) ? 1 : leadDays,
          required: el.dataset.required === 'true',
          attrDate: el.dataset.attrDate,
          attrTime: el.dataset.attrTime,
        };

        const earliest = new Date();
        earliest.setDate(earliest.getDate() + slot.leadDays);
        // Skip past any closed days so the calendar opens on a bookable date.
        slot.minDate = PickupScheduler.formatDate(this.firstOpenOnOrAfter(earliest));
        dateInput.min = slot.minDate;

        // A stored value must not survive a shorter lead time or a closed day.
        if (dateInput.value) {
          const stored = PickupScheduler.parseLocalDate(dateInput.value);
          if (!stored || dateInput.value < slot.minDate || !this.isOpenDay(stored)) {
            dateInput.value = '';
          }
        }

        this.buildTimeOptions(slot);

        const onChange = this.onChange.bind(this);
        dateInput.addEventListener('change', onChange);
        timeSelect.addEventListener('custom-select:change', onChange);

        return slot;
      }

      /* Builds the custom dropdown's option list, then tells it to rebind. */
      buildTimeOptions(slot) {
        const list = slot.timeSelect.querySelector('[role="listbox"]');
        const label = slot.timeSelect.querySelector('[data-cs-label]');
        const saved = slot.timeInput.value;
        const items = [];

        for (let minutes = this.openHour * 60; minutes <= this.closeHour * 60; minutes += this.interval) {
          const hour24 = Math.floor(minutes / 60);
          const minute = minutes % 60;
          const suffix = hour24 >= 12 ? 'PM' : 'AM';
          const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
          const text = `${hour12}:${`${minute}`.padStart(2, '0')} ${suffix}`;
          const selected = text === saved ? 'true' : 'false';
          items.push(
            `<li class="custom-select__option" role="option" tabindex="-1" aria-selected="${selected}" data-value="${text}" data-label="${text}">${text}</li>`
          );
        }

        list.innerHTML = items.join('');
        if (saved) label.textContent = saved;
        if (typeof slot.timeSelect.refresh === 'function') slot.timeSelect.refresh();
      }

      isComplete(slot) {
        return Boolean(slot.dateInput.value && slot.timeInput.value);
      }

      /* Every slot the cart actually needs must be filled in. */
      get allRequiredComplete() {
        if (this.addressMissing) return false;
        return this.slots.filter((s) => s.required).every((s) => this.isComplete(s));
      }

      onChange() {
        /* Clear any closed-day pick before it can reach the cart. The native
           date input cannot grey out individual days, so this is the guard. */
        let rejected = false;
        this.slots.forEach((slot) => {
          if (!slot.dateInput.value) return;
          const chosen = PickupScheduler.parseLocalDate(slot.dateInput.value);
          if (!this.isOpenDay(chosen)) {
            slot.dateInput.value = '';
            rejected = true;
          }
        });

        this.syncCheckoutState();
        if (rejected) this.setStatus(this.closedDayMessage(), true);
        else if (this.addressMissing) this.setStatus('Please add a delivery address.', true);

        const attributes = {
          'Order type': this.orderType,
          Fulfilment: this.fulfilment,
          'Delivery address': this.fulfilment === 'Delivery' ? this.deliveryAddress : '',
        };
        let anyFilled = false;

        this.slots.forEach((slot) => {
          if (this.isComplete(slot)) {
            attributes[slot.attrDate] = slot.dateInput.value;
            attributes[slot.attrTime] = slot.timeInput.value;
            anyFilled = true;
          } else {
            // Clear a half-filled or abandoned slot rather than leaving a stale
            // time on the order.
            attributes[slot.attrDate] = '';
            attributes[slot.attrTime] = '';
          }
        });

        if (!rejected) this.setStatus('Saving…');

        fetch(this.updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }),
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
            return response.json();
          })
          .then(() => {
            if (!rejected) this.setStatus('Saved.');
            document.dispatchEvent(new CustomEvent('pickup-scheduler:updated'));
          })
          .catch(() => {
            // Not saved, so checkout must not proceed as though it were.
            this.setStatus('Could not save your time. Please try again.', true);
            this.syncCheckoutState(true);
          });
      }

      setStatus(message, isError = false) {
        if (!this.status) return;
        this.status.textContent = message;
        this.status.classList.toggle('pickup-scheduler__status--error', isError);
      }

      syncCheckoutState(forceBlock = false) {
        const ready = this.allRequiredComplete && !forceBlock;

        this.slots.forEach((slot) => {
          slot.el.classList.toggle('pickup-slot--incomplete', slot.required && !this.isComplete(slot));
        });

        document.querySelectorAll('[data-requires-pickup-slot]').forEach((button) => {
          button.disabled = !ready;
          button.setAttribute('aria-disabled', String(!ready));
          button.classList.toggle('pickup-blocked', !ready);
        });

        document.querySelectorAll('[data-pickup-slot-warning]').forEach((warning) => {
          warning.hidden = ready;
        });
      }
    }
  );
}
